import type { RuntimeConfig } from '../config/runtime';
import { runtimeConfig } from '../config/runtime';
import type { QuoteFormInput, QuoteFormPayload } from '../schema/quoteFormSchema';

export type SubmissionErrorKind =
  | 'validation'
  | 'rate-limit'
  | 'server'
  | 'network';

export type FieldErrors = Partial<Record<keyof QuoteFormInput, string>>;

export class QuoteSubmissionError extends Error {
  readonly kind: SubmissionErrorKind;
  readonly fieldErrors: FieldErrors;

  constructor(
    kind: SubmissionErrorKind,
    message: string,
    fieldErrors: FieldErrors = {},
  ) {
    super(message);
    this.name = 'QuoteSubmissionError';
    this.kind = kind;
    this.fieldErrors = fieldErrors;
  }
}

type MockScenario = 'success' | 'validation' | 'rate-limit' | 'server-error';

const getMockScenario = (): MockScenario => {
  if (typeof window === 'undefined') return 'success';

  const scenario = new URLSearchParams(window.location.search).get('mock');
  if (
    scenario === 'validation' ||
    scenario === 'rate-limit' ||
    scenario === 'server-error'
  ) {
    return scenario;
  }

  return 'success';
};

const wait = (milliseconds: number) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const submitToMock = async (
  config: RuntimeConfig,
  scenario: MockScenario,
) => {
  await wait(config.mockDelayMs);

  if (scenario === 'validation') {
    throw new QuoteSubmissionError(
      'validation',
      'Revisá los datos marcados e intentá nuevamente.',
      { email: 'Este email no puede utilizarse.' },
    );
  }

  if (scenario === 'rate-limit') {
    throw new QuoteSubmissionError(
      'rate-limit',
      'Realizaste varios intentos. Esperá unos minutos antes de volver a enviar.',
    );
  }

  if (scenario === 'server-error') {
    throw new QuoteSubmissionError(
      'server',
      'No pudimos enviar tu solicitud. Intentá nuevamente en unos minutos.',
    );
  }
};

const readJson = async (response: Response): Promise<Record<string, unknown>> => {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const toFieldErrors = (value: unknown): FieldErrors => {
  if (!value || typeof value !== 'object') return {};

  return Object.fromEntries(
    Object.entries(value).filter(([, message]) => typeof message === 'string'),
  ) as FieldErrors;
};

export const createQuoteSubmitter = (
  config: RuntimeConfig,
  mockScenario: () => MockScenario = getMockScenario,
) => {
  return async (payload: QuoteFormPayload): Promise<void> => {
    if (config.apiMode === 'mock') {
      await submitToMock(config, mockScenario());
      return;
    }

    let response: Response;

    try {
      response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch {
      throw new QuoteSubmissionError(
        'network',
        'No pudimos conectarnos. Revisá tu conexión e intentá nuevamente.',
      );
    }

    if (response.ok) return;

    const body = await readJson(response);

    if (response.status === 400 && body.code === 'VALIDATION_ERROR') {
      throw new QuoteSubmissionError(
        'validation',
        'Revisá los datos marcados e intentá nuevamente.',
        toFieldErrors(body.fieldErrors),
      );
    }

    if (response.status === 429) {
      throw new QuoteSubmissionError(
        'rate-limit',
        'Realizaste varios intentos. Esperá unos minutos antes de volver a enviar.',
      );
    }

    throw new QuoteSubmissionError(
      'server',
      'No pudimos enviar tu solicitud. Intentá nuevamente en unos minutos.',
    );
  };
};

export const submitQuote = createQuoteSubmitter(runtimeConfig);
